import { Check, Copy, KeyRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  DEALERSHIP_TIMEZONE,
  TEMPORARY_PASSWORD_TTL_HOURS,
  type InvitationOutcome,
} from "../../../shared/contracts.js";
import { formatDeadline } from "../../../shared/deadline.js";
import { Button } from "../../ui/Button.js";
import { Dialog, DialogActions } from "../../ui/Dialog.js";

/**
 * The one place in the interface a temporary password is ever shown (spec
 * §2: an administrator "can create accounts, issue a temporary password …
 * reset passwords"; the person "must replace a temporary password at first
 * login").
 *
 * The server has already emailed the password to the person (owner
 * decision 2026-09-16; it works for 72 hours) and says here whether that
 * email went out. When it did, the dialog is a receipt: where it went and
 * until when the password works. When it did not — mail unreachable, no
 * address — the dialog says so, with the mailer's reason, and the
 * administrator hands the password over another way; the account exists
 * either way.
 *
 * The password arrives as a prop from the page's component state and lives
 * nowhere else: not in a query or mutation cache, the URL, storage or a log.
 * When the dialog closes the page clears that state, so the value leaves the
 * document with it. The copy action writes to the clipboard on request only.
 */

export interface TemporaryPasswordDialogProps {
  /** The password to reveal; `null` keeps the dialog closed. */
  password: string | null;
  /** What became of the email that carried it; `null` only while closed. */
  invitation: InvitationOutcome | null;
  /** Whose account it is (display name). */
  accountName: string;
  /** Why it exists: a new account or a reset. */
  reason: "created" | "reset";
  onClose: () => void;
}

/**
 * "works for 72 hours, until Saturday, September 19, 2026, 7:00 AM (Central
 * Daylight Time); they'll choose their own password at first sign-in." —
 * the deadline spelled out the way the email and the sign-in refusal spell
 * it, so the administrator, the person and the refusal read the same words.
 * The zone is the interface's fixed `America/Chicago`, like every instant the
 * pages show; the server's `DEALERSHIP_TIMEZONE` governs only what it
 * prints, and the README says to leave it at that same default.
 */
function worksUntil(invitation: InvitationOutcome): string {
  const deadline = formatDeadline(new Date(invitation.expiresAt), DEALERSHIP_TIMEZONE);
  return `works for ${TEMPORARY_PASSWORD_TTL_HOURS} hours, until ${deadline}; they'll choose their own password at first sign-in.`;
}

/**
 * Why the email did not go: "The email could not be sent to <address>
 * (<the mailer's reason>)." — or, with no address to send to, one plain
 * sentence, since the server's reason is then a sentence of its own and
 * "(The account has no email address.)." would nest one inside another. A
 * reason that ends in a period loses it inside the brackets for the same
 * reason.
 */
function notSent(invitation: InvitationOutcome): string {
  if (!invitation.to) return "This account has no email address, so nothing was sent.";
  const reason = invitation.reason?.replace(/\.$/, "");
  return `The email could not be sent to ${invitation.to}${reason ? ` (${reason})` : ""}.`;
}

type CopyState = "idle" | "copied" | "failed";

export function TemporaryPasswordDialog({
  password,
  invitation,
  accountName,
  reason,
  onClose,
}: TemporaryPasswordDialogProps) {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const fieldRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);

  // A fresh reveal starts with a fresh copy state.
  useEffect(() => {
    setCopyState("idle");
  }, [password]);

  const copy = async () => {
    if (password === null) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopyState("copied");
    } catch {
      // No clipboard permission or API: select the text so a manual copy works.
      fieldRef.current?.select();
      setCopyState("failed");
    }
  };

  const lead =
    reason === "created"
      ? `${accountName}'s account is ready.`
      : `${accountName}'s password was reset and their sessions were ended.`;

  return (
    <Dialog
      open={password !== null}
      onClose={onClose}
      title="Temporary password"
      description={`${lead} This password is shown only once — it disappears when you close this window.`}
      size="small"
      initialFocusRef={doneRef}
      closeLabel="Close and forget the password"
    >
      <div className="temporary-password">
        <div className="temporary-password__field">
          <label htmlFor="temporary-password-value" className="field__label">
            <KeyRound
              aria-hidden="true"
              focusable="false"
              className="temporary-password__icon"
            />
            Temporary password for {accountName}
          </label>
          <input
            ref={fieldRef}
            id="temporary-password-value"
            className="text-input temporary-password__value"
            type="text"
            readOnly
            value={password ?? ""}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
        <div className="temporary-password__copy-row">
          <Button
            variant="secondary"
            icon={copyState === "copied" ? <Check /> : <Copy />}
            onClick={() => void copy()}
          >
            {copyState === "copied" ? "Copied" : "Copy password"}
          </Button>
          <span
            role="status"
            aria-live="polite"
            className="temporary-password__status"
          >
            {copyState === "copied"
              ? "Copied to the clipboard."
              : copyState === "failed"
                ? "Copying isn't available here — the password is selected, press Ctrl+C or ⌘C."
                : ""}
          </span>
        </div>
        {invitation === null ? null : invitation.state === "sent" ? (
          <p
            role="note"
            aria-label="Sign-in email"
            className="temporary-password__instructions"
          >
            Emailed to {invitation.to}. It {worksUntil(invitation)}
          </p>
        ) : (
          <p role="alert" className="form-alert temporary-password__instructions">
            {notSent(invitation)} Give them this password directly — it{" "}
            {worksUntil(invitation)}
          </p>
        )}
      </div>
      <DialogActions>
        <Button ref={doneRef} variant="primary" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
